import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { sectionAnimations } from '@platform/helpers/animations.helper';
import { InputType } from 'emma-common-ts/emma';
import { noop } from 'lodash';

import { EMMAFormElementComponent } from './emma-form-element.component';
import { EMMAInputComponent } from './emma-input.component';
import { EMMASelectComponent } from './emma-select.component';

export interface Item {
  [k: string]: any;
}

export interface Option {
  value: string | number | null;
  label: string;
}

const EMMA_MULTI_TYPE_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAMultiTypeInputComponent),
  multi: true,
};

const EMMA_SELECT_VALIDATOR: any = {
  provide: NG_VALIDATORS,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAMultiTypeInputComponent),
  multi: true,
};

@Component({
    selector: 'emma-multitypeinput',
    templateUrl: './emma-multitypeinput.component.html',
    styleUrls: ['./emma-multitypeinput.component.scss'],
    animations: [...sectionAnimations],
    providers: [
        EMMA_MULTI_TYPE_INPUT_CONTROL_VALUE_ACCESSOR,
        EMMA_SELECT_VALIDATOR,
        {
            provide: EMMAFormElementComponent,
            useExisting: EMMAMultiTypeInputComponent,
        },
    ],
    standalone: false
})
export class EMMAMultiTypeInputComponent
  extends EMMAFormElementComponent
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor, Validator
{
  @ViewChildren(EMMAInputComponent) inputComponents?: QueryList<EMMAInputComponent>;
  @ViewChildren(EMMASelectComponent) selectComponents?: QueryList<EMMASelectComponent>;

  // Number of items to show as initial state
  @Input() shownByDefault = 0;
  // Text of the create button, no text -> no button
  @Input() createButton: string | undefined;
  // Label for the Type input
  @Input() typeLabel: string | undefined;
  // Label for the Value input
  @Input() valueLabel: string | undefined;
  // Placeholder for the Type input
  @Input() typePlaceholder: string | undefined;
  // Placeholder for the Value input
  @Input() valuePlaceholder: string | undefined;
  // Placeholder for the Value input, based on current Type
  @Input() valuePlaceholders: { [type: string]: string } | undefined;
  @Input() itemTypes: Option[] = [];
  @Input() itemValues: string[] = [];
  @Input() typeKey = 'type';
  @Input() valueKey = 'value';
  // Allow empty Values = show Add button
  @Input() allowEmptyValue = false;
  // Allow empty Types = show Add button
  @Input() allowEmptyType = false;
  // Allow duplicated Values when suggesting
  @Input() suggestUsedValues = false;
  // Allow remove items
  @Input() allowRemove = true;
  // Type is readonly
  @Input() readonlyKeys = false;
  // Type is ignored
  @Input() onlyValues = false;
  // Types are suggestions not a select
  @Input() typesAsSuggestions = false;
  // Values are suggestions not a select
  @Input() valuesAsSuggestions = false;
  // Just suggestions or blank
  @Input() allowTypeSuggestionsOnly = false;
  @Input() allowValueSuggestionsOnly = false;
  // Customise the view
  @Input() invertInputOrder = false;
  @Input() onlyFirstLabel = false;
  //Input
  @Input() typeInputType: InputType = 'text';
  @Input() typeShowHelpMessageAtError = false;
  @Input() typeHelpMessageAtError = '';
  @Input() valueInputType: InputType = 'text';
  @Input() valueShowHelpMessageAtError = false;
  @Input() valueHelpMessageAtError = '';

  availableTypes: Option[] = [];

  @Output() override $change = new EventEmitter<Item[]>();
  @Output() override $blur = new EventEmitter<Item[]>();

  cachedTypeOptions: Option[][] = [];
  cachedValues: string[][] = [];
  cachedLabels: string[] = [];

  // Validator interface
  @Input() minlength = 0;
  @Input() maxlength = Infinity;

  @Input() validateValuesFn = null;

  get isValid(): boolean {
    let isValid = true;
    if (this.itemTypes.length && !this.typesAsSuggestions) {
      isValid = isValid && (this.selectComponents?.toArray()?.filter((c) => c.name.startsWith('multitypeinput.select.first')).every((c) => c.isValid) ?? false);
    } else {
      isValid = isValid && (this.inputComponents?.toArray()?.filter((c) => c.name.startsWith('multitypeinput.input.first')).every((c) => c.isValid) ?? false);
    }
    if (this.itemValues.length && !this.valuesAsSuggestions) {
      isValid = isValid && (this.selectComponents?.toArray()?.filter((c) => c.name.startsWith('multitypeinput.select.second')).every((c) => c.isValid) ?? false);
    } else {
      isValid = isValid && (this.inputComponents?.toArray()?.filter((c) => c.name.startsWith('multitypeinput.input.second')).every((c) => c.isValid) ?? false);
    }
    return isValid;
  }

  // ControlValueAccesor interface
  private innerValue: Item[] = [];
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  override ngOnInit(): void {
    this.updateTypes();
  }
  override ngOnChanges(changes: SimpleChanges): void {
    if ('onlyValues' in changes) {
      if (this.onlyValues) {
        this.typesAsSuggestions = true;
        this.allowEmptyType = true;
      }
    }
    this.updateTypes();
  }

  // get accessor
  get value(): Item[] {
    return this.innerValue;
  }

  // set accessor including call the onchange callback
  set value(v: Item[]) {
    if (v !== this.innerValue) {
      this.innerValue = v || [];
      this.onChangeCallback(this.innerValue);
    }
  }

  // Set touched on blur
  onBlur() {
    this.onTouchedCallback();
    this.$blur.emit(this.value);
  }

  // From ControlValueAccessor interface
  writeValue(value: Item[]) {
    if (value !== this.innerValue) {
      this.value = value;
      this.updateTypes();
    }
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  updateItem() {
    this.updateTypes();
    this.onChangeCallback(this.innerValue);
    this.$change.emit(this.innerValue.slice(0));
  }
  onAdd(much = 1) {
    for (let i = 0; i < much; ++i) {
      const type = this.availableTypes.length && !this.typesAsSuggestions ? this.availableTypes[i].value : '';
      const item: Item = {
        [this.typeKey]: type,
        [this.valueKey]: this.itemValues.length ? null : '',
      };
      this.innerValue.push(item);
    }
    this.updateTypes();
    this.onChangeCallback(this.innerValue);
    this.$change.emit(this.innerValue.slice(0));
  }
  onRemove(index: number) {
    this.innerValue.splice(index, 1);
    this.updateTypes();
    this.onChangeCallback(this.innerValue);
    this.$change.emit(this.innerValue.slice(0));
  }

  private updateTypes() {
    const { typeKey, valueKey, value, itemTypes, itemValues } = this;
    const selectedTypes = value.map((it) => it[typeKey]);
    const selectedValues = value.map((it) => it[valueKey]);
    const cachedTypeOptions = [];
    const cachedValues = [];
    const cachedLabels = [];
    for (const item of value) {
      if (itemTypes.length) {
        const ownType = itemTypes.find((it) => it.value === item[typeKey]);
        cachedTypeOptions.push(
          itemTypes.filter(
            (it) => !selectedTypes.includes(it.value ?? '') || (ownType && ownType.value === it.value)
          )
        );
        cachedLabels.push(ownType ? ownType.label : '');
      }
      if (itemValues.length) {
        const ownValue = item[valueKey];
        cachedValues.push(itemValues.filter((it) => !selectedValues.includes(it) || ownValue === it));
      }
    }
    this.availableTypes = itemTypes.filter((it) => !selectedTypes.includes(it.value ?? ''));
    this.cachedTypeOptions = cachedTypeOptions;
    this.cachedValues = cachedValues;
    this.cachedLabels = cachedLabels;

    if (this.required || this.shownByDefault) {
      this.checkShown();
    }
  }

  showCreateButton() {
    return (
      // can create
      this.createButton &&
      // type
      (this.allowEmptyType || this.availableTypes.length > 0) &&
      // value
      (this.allowEmptyValue || this.innerValue.every((it) => it[this.valueKey])) &&
      // types left
      (!this.itemTypes.length || !this.allowTypeSuggestionsOnly || this.availableTypes.length)
    );
  }

  checkShown() {
    if (this.required || this.shownByDefault) {
      const required: number = 'boolean' === typeof this.required ? 1 : Number(this.required);
      const shown = Math.max(required, this.shownByDefault);
      if (this.innerValue && this.innerValue.length < shown && this.availableTypes) {
        this.onAdd(Math.min(shown - this.innerValue.length, this.availableTypes.length));
      }
    }
  }

  override validate: ValidatorFn = (control: AbstractControl) => {
    const validators: ValidatorFn[] = [];
    if (this.required) {
      validators.push(Validators.required);
      if (Array.isArray(control.value)) {
        validators.push(Validators.minLength(1));
      }
    }
    if (this.maxlength < Infinity) {
      validators.push(Validators.maxLength(this.maxlength));
    }
    if (this.minlength > 0) {
      validators.push(Validators.minLength(this.minlength));
    }
    if (this.validateFn) {
      validators.push(this.validateFn);
    }
    if (validators.length) {
      const validator = Validators.compose(validators);
      return validator ? validator(control) : null;
    }
    return null;
  };
}
